import api from './api';
import { rootStore } from '../_store/index';

class DashboardService {
  /**
   * Get comprehensive dashboard statistics
   */
  async getDashboardStats(dateFilter = '7days') {
    try {
      const authStore = rootStore?.authStore;

      // Only make API calls for data the user has permission to access
      const promises = [];
      
      // Event analytics - requires event_management permission
      if (authStore?.hasPermission('event_management')) {
        promises.push(this.getEventAnalytics(dateFilter));
      } else {
        promises.push(Promise.resolve({
          total: 0,
          active: 0,
          completed: 0,
          upcomingEvents: []
        }));
      }

      // Volunteer analytics - requires user_management permission
      if (authStore?.hasPermission('user_management')) {
        promises.push(this.getVolunteerAnalytics());
      } else {
        promises.push(Promise.resolve({
          total: 0,
          active: 0,
          newThisMonth: 0,
          totalHours: 0
        }));
      }

      // Patient analytics - requires screening_data permission
      if (authStore?.hasPermission('screening_data')) {
        promises.push(this.getPatientAnalytics(dateFilter));
        promises.push(this.getLPATestStats(dateFilter));
      } else {
        promises.push(Promise.resolve({
          completedVisits: 0,
          pendingReview: 0,
          highRisk: 0,
          newThisWeek: 0
        }));
        promises.push(Promise.resolve({
          total: 0,
          pending: 0,
          completed: 0,
          scheduled: 0
        }));
      }

      const [
        eventAnalytics,
        volunteerAnalytics,
        patientAnalytics,
        lpaTestStats
      ] = await Promise.all(promises);

      return {
        success: true,
        data: {
          events: eventAnalytics,
          volunteers: volunteerAnalytics,
          patients: patientAnalytics,
          lpaTests: lpaTestStats
        }
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Return mock data if API fails
      return this.getMockDashboardStats();
    }
  }

  /**
   * Get event analytics with date filtering
   */
  async getEventAnalytics(dateFilter = '7days') { // eslint-disable-line no-unused-vars
    try {
      // TODO: This endpoint doesn't exist yet in the backend
      // For now, return zeroed structure (no fake numbers in production dashboards)
      console.log('Event analytics endpoint not implemented yet, returning zero data');
      return {
        total: 0,
        active: 0,
        completed: 0,
        upcomingEvents: []
      };
      
      // When the backend endpoint is ready, uncomment this:
      // const response = await api.get(`/events/analytics/statistics?dateRange=${dateFilter}`);
      // return response.data || {};
    } catch (error) {
      console.error('Error fetching event analytics:', error);
      return {
        total: 0,
        active: 0,
        completed: 0,
        upcomingEvents: []
      };
    }
  }

  /**
   * Get volunteer analytics
   */
  async getVolunteerAnalytics() {
    try {
      // TODO: This endpoint doesn't exist yet in the backend
      // For now, return zeroed structure
      console.log('Volunteer analytics endpoint not implemented yet, returning zero data');
      return {
        total: 0,
        active: 0,
        newThisMonth: 0,
        totalHours: 0
      };
      
      // When the backend endpoint is ready, uncomment this:
      // const response = await api.get('/user-management/analytics/volunteers');
      // return response.data || {};
    } catch (error) {
      console.error('Error fetching volunteer analytics:', error);
      return {
        total: 0,
        active: 0,
        newThisMonth: 0,
        totalHours: 0
      };
    }
  }

  /**
   * Get patient analytics with date filtering
   */
  async getPatientAnalytics(dateFilter = '7days') {
    try {
      // Map date filter to timeRange parameter for backend
      let timeRange;
      switch (dateFilter) {
        case '7days':
          timeRange = 'week';
          break;
        case '14days':
          timeRange = 'twoWeeks';
          break;
        case '30days':
          timeRange = 'month';
          break;
        case '90days':
          timeRange = 'quarter';
          break;
        default:
          timeRange = 'week';
      }
      
      // Get filtered analytics - backend now returns both filtered (total) and allTime counts
      const response = await api.get(`/screening/analytics/patients?timeRange=${timeRange}`);
      
      if (response.success && response.data) {
        const data = response.data;
        
        // Calculate high risk count from byRisk distribution
        const byRisk = data.byRisk || {};
        const highRiskCount = (byRisk.high || 0) + (byRisk.High || 0) + 
                             (byRisk.critical || 0) + (byRisk.Critical || 0) + 
                             (byRisk.very_high || 0);
        
        return {
          completedVisits: data.completedVisits || 0, // Completed visits filtered by date range
          screeningIntake: data.total || 0, // Filtered by date range
          pendingReview: data.pending || 0,
          completed: data.completed || 0,
          pending: data.pending || data.queueStats?.pending_review || 0,
          needsAttention: data.needsAttention || data.queueStats?.needs_attention || 0,
          highRisk: highRiskCount,
          newThisWeek: data.new || data.queueStats?.new_patient || 0,
          byRisk: byRisk,
          byGender: data.byGender || {},
          byAge: data.byAge || {}
        };
      }
      return this.getMockPatientAnalytics();
    } catch (error) {
      console.error('Error fetching patient analytics:', error);
      return this.getMockPatientAnalytics();
    }
  }

  /**
   * Get mock patient analytics as fallback
   */
  getMockPatientAnalytics() {
    return {
      completedVisits: 0,
      screeningIntake: 0,
      pendingReview: 0,
      completed: 0,
      pending: 0,
      needsAttention: 0,
      highRisk: 0,
      newThisWeek: 0,
      byRisk: {},
      byGender: {},
      byAge: {}
    };
  }

  /**
   * Get LPA test statistics with date filtering
   */
  async getLPATestStats(dateFilter = '7days') { // eslint-disable-line no-unused-vars
    try {
      // TODO: Update API call to include date range filtering
      // const response = await api.get(`/screening/lpa-tests?dateRange=${dateFilter}`);
      const response = await api.get('/screening/lpa-tests');
      const tests = response.tests || [];
      
      return {
        total: tests.length,
        pending: tests.filter(test => test.status === 'pending').length,
        completed: tests.filter(test => test.status === 'completed').length,
        scheduled: tests.filter(test => test.status === 'scheduled').length
      };
    } catch (error) {
      console.error('Error fetching LPA test stats:', error);
      return {
        total: 0,
        pending: 0,
        completed: 0,
        scheduled: 0
      };
    }
  }

  /**
   * Get quick stats for cards
   */
  async getQuickStats(dateFilter = '7days') { // eslint-disable-line no-unused-vars
    try {
      const authStore = rootStore?.authStore;

      // Check permissions before calling getDashboardStats
      if (!authStore?.hasPermission('dashboard')) {
        // Return empty stats for users without dashboard permission
        return {
          totalScreenings: 0,
          screeningIntake: 0,
          pendingLPATests: 0,
          events: 0
        };
      }

      const stats = await this.getDashboardStats(dateFilter);
      
      // Calculate date range based on filter
      const endDate = new Date();
      const startDate = new Date();
      
      switch (dateFilter) {
        case '7days':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case '14days':
          startDate.setDate(endDate.getDate() - 14);
          break;
        case '30days':
          startDate.setDate(endDate.getDate() - 30);
          break;
        default:
          startDate.setDate(endDate.getDate() - 7);
      }
      
      // For now, we'll use mock calculations based on date range
      // In production, these would filter actual data from the API
      const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      const dailyAvgIntake = (stats.data.patients.newThisWeek ?? 0) / 7; // Weekly to daily
      const dailyAvgLPA = (stats.data.lpaTests.pending ?? 0) / 30; // Monthly average
      
      return {
        totalScreenings: stats.data.patients.completedVisits ?? stats.data.patients.totalScreenings ?? 0,
        screeningIntake: Math.round(dailyAvgIntake * daysDiff),
        pendingLPATests: Math.round(dailyAvgLPA * daysDiff),
        events: this.getEventsInDateRange(dateFilter, stats.data.events)
      };
    } catch (error) {
      console.error('Error fetching quick stats:', error);
      // Return zeroed structure
      return this.getMockQuickStats(dateFilter);
    }
  }

  /**
   * Calculate events within date range
   */
  getEventsInDateRange(dateFilter, eventsData) {
    // Mock calculation - in production this would filter actual event data
    const baseEvents = eventsData?.active ?? 0;
    if (!baseEvents) return 0;
    switch (dateFilter) {
      case '7days':
        return Math.round(baseEvents * 0.3);
      case '14days':
        return Math.round(baseEvents * 0.6);
      case '30days':
        return baseEvents;
      default:
        return Math.round(baseEvents * 0.3);
    }
  }

  /**
   * Get mock quick stats with date filtering
   */
  getMockQuickStats() {
    return {
      totalScreenings: 0,
      screeningIntake: 0,
      pendingLPATests: 0,
      events: 0
    };
  }

  /**
   * Mock data fallback
   */
  getMockDashboardStats() {
    return {
      success: true,
      data: {
        events: { total: 0, active: 0, completed: 0, upcomingEvents: [] },
        volunteers: { total: 0, active: 0, newThisMonth: 0, totalHours: 0 },
        patients: { completedVisits: 0, pendingReview: 0, highRisk: 0, newThisWeek: 0 },
        lpaTests: { total: 0, pending: 0, completed: 0, scheduled: 0 }
      }
    };
  }
}

export default new DashboardService();
